// src/services/companionAccess.js
//
// Who is allowed to talk to Marlow, as a pure function so it can be tested.
//
// Until the move to the Auth Hub, this app's AuthContext rebuilt a `user` from
// localStorage ("artOfStory_isSignedIn") with no live Firebase session behind
// it, and did so again ~100ms after returning from the old sign-in page with
// ?auth=success, after `loading` had already gone false. Fine for a name in
// the header; not fine for Marlow, who needs a real ID token, or every
// Knowledge Hub call 401s while the UI insists you are signed in. AuthContext
// now reads only the Firebase session, but the gate stays on the session
// itself so Marlow stays correct if that ever changes again.
//
// Profound Questions shipped exactly this failure — a legacy session on a new
// bundle showed signed-in UI while every hub sync silently failed. So the gate
// here is the Firebase session itself, not `isAuthenticated`.
//
// Australian English throughout.

/**
 * @param {object}  state
 * @param {boolean} state.authLoading   AuthContext's `loading` — auth has not settled yet.
 * @param {boolean} state.isAuthenticated  AuthContext's `!!user`, which localStorage can fake.
 * @param {object|null} state.firebaseUser `auth.currentUser` — the only proof of a real token.
 * @returns {{canTalk: boolean, staleLocalSession: boolean, uid: string|null}}
 */
export function resolveCompanionAccess({ authLoading, isAuthenticated, firebaseUser }) {
  const hasLiveSession = Boolean(firebaseUser);

  if (authLoading) {
    return { canTalk: false, staleLocalSession: false, uid: null };
  }

  return {
    canTalk: hasLiveSession,
    // Signed in as far as this device is concerned, but with nothing behind
    // it. Worth naming, so the person is not looped through a sign-in that
    // appears to have already worked.
    staleLocalSession: isAuthenticated && !hasLiveSession,
    // The thread is keyed on uid; keying it on a phantom localStorage uid
    // would strand a conversation under an id no token will ever match.
    uid: hasLiveSession ? (firebaseUser.uid || null) : null,
  };
}

export default resolveCompanionAccess;
